import {
  QUESTIONS_PER_ROUND,
  type GuessReveal,
  type LatLng,
  type PublicQuestion,
  type ReviewEntry,
  type RoundMode,
  type RoundSummary,
  type SpecialInfo,
  type Sport,
} from '@geosport/shared';
import { create } from 'zustand';
import { track } from '../lib/analytics';
import { ApiError, getPlayerToken } from '../lib/api';
import { getBackend } from '../lib/backend';
import { savePractice } from '../lib/practice';
import { isRecord, readJson, removeKey, STORAGE_KEYS, writeJson } from '../lib/storage';

export type Phase =
  'boot' | 'loading' | 'idle' | 'where' | 'when' | 'submitting' | 'reveal' | 'done' | 'error';

interface StoredProgress {
  date: string;
  progressToken: string;
  reveals: GuessReveal[];
}

interface StoredPractice extends StoredProgress {
  mode: RoundMode;
  sport: Sport | null;
  /** The special being played, if any. */
  special?: SpecialInfo | null;
  puzzleNo: number;
  questions: PublicQuestion[];
}

const isStoredProgress = (v: unknown): v is StoredProgress =>
  isRecord(v) &&
  typeof v['date'] === 'string' &&
  typeof v['progressToken'] === 'string' &&
  Array.isArray(v['reveals']);

const isStoredPractice = (v: unknown): v is StoredPractice => {
  if (!isRecord(v) || !isStoredProgress(v)) return false;
  const r: Record<string, unknown> = v;
  return (
    (r['mode'] === 'archive' || r['mode'] === 'random') &&
    Array.isArray(r['questions']) &&
    typeof r['puzzleNo'] === 'number'
  );
};

/** Today's ranked round, remembered while a practice round is active. */
export interface DailyStatus {
  date: string;
  launchDate: string;
  puzzleNo: number;
  summary: RoundSummary | null;
  review: ReviewEntry[] | null;
}

export interface RoundState {
  mode: RoundMode;
  sport: Sport | null;
  phase: Phase;
  error: string | null;
  puzzleNo: number;
  date: string;
  questions: PublicQuestion[];
  progressToken: string | null;
  position: number;
  pin: LatLng | null;
  year: number | null;
  reveals: GuessReveal[];
  summary: RoundSummary | null;
  review: ReviewEntry[] | null;
  yesterdayAvg: number | null;
  playersToday: number | null;
  daily: DailyStatus | null;
  /** The special being played (special rounds only). */
  special: SpecialInfo | null;
  /** Specials promoted today, from the daily load. */
  activeSpecials: SpecialInfo[];

  load: () => Promise<void>;
  start: () => void;
  startArchive: (date: string) => Promise<void>;
  startRandom: (sport: Sport | null) => Promise<void>;
  startSpecial: (slug: string) => Promise<void>;
  exitPractice: () => Promise<void>;
  setPin: (pin: LatLng) => void;
  lockLocation: () => void;
  setYear: (year: number) => void;
  lockYear: () => Promise<void>;
  next: () => void;
  reset: () => void;
}

type Setter = (partial: Partial<RoundState>) => void;

const initial = {
  mode: 'daily' as RoundMode,
  sport: null as Sport | null,
  phase: 'boot' as Phase,
  error: null,
  puzzleNo: 0,
  date: '',
  questions: [] as PublicQuestion[],
  progressToken: null,
  position: 1,
  pin: null,
  year: null,
  reveals: [] as GuessReveal[],
  summary: null,
  review: null,
  yesterdayAvg: null,
  playersToday: null,
  daily: null,
  special: null as SpecialInfo | null,
  activeSpecials: [] as SpecialInfo[],
};

function persistDaily(s: Pick<RoundState, 'date' | 'progressToken' | 'reveals'>): void {
  if (!s.progressToken) return;
  writeJson(STORAGE_KEYS.progress, {
    date: s.date,
    progressToken: s.progressToken,
    reveals: s.reveals,
  } satisfies StoredProgress);
}

function persistPractice(
  s: Pick<
    RoundState,
    'mode' | 'sport' | 'date' | 'puzzleNo' | 'questions' | 'progressToken' | 'reveals' | 'special'
  >,
): void {
  if (!s.progressToken) return;
  writeJson(STORAGE_KEYS.practiceProgress, {
    mode: s.mode,
    sport: s.sport,
    special: s.special,
    date: s.date,
    puzzleNo: s.puzzleNo,
    questions: s.questions,
    progressToken: s.progressToken,
    reveals: s.reveals,
  } satisfies StoredPractice);
}

/** Per-question review built from the reveals of the round just played. */
export function reviewFromReveals(
  reveals: GuessReveal[],
  questions: PublicQuestion[],
): ReviewEntry[] {
  return reveals.map((r) => {
    const q = questions[r.position - 1];
    return {
      position: r.position,
      sport: q?.sport ?? 'other',
      clue: q?.clue ?? '',
      answer: r.answer,
      story: r.story,
      distanceKm: r.distanceKm,
      yearDiff: r.yearDiff,
      wherePts: r.wherePts,
      whenPts: r.whenPts,
      basePts: r.basePts,
      multiplier: r.multiplier,
      questionPts: r.questionPts,
    };
  });
}

const message = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

/** True while a replay or random round owns the screen: the daily loader must not clobber it. */
const practiceActive = (get: () => RoundState): boolean => {
  const s = get();
  return s.mode !== 'daily' && s.phase !== 'boot' && s.phase !== 'error';
};

async function loadRound(set: Setter, get: () => RoundState): Promise<void> {
  if (!practiceActive(get)) set({ phase: 'loading', error: null, mode: 'daily', sport: null });
  try {
    const backend = await getBackend();
    const today = await backend.today();
    const daily: DailyStatus = {
      date: today.date,
      launchDate: today.launchDate,
      puzzleNo: today.puzzleNo,
      summary: today.completed,
      review: today.review,
    };
    if (practiceActive(get)) {
      // A practice round started meanwhile (direct visit to /play/random): keep it, refresh today's status.
      set({
        daily,
        yesterdayAvg: today.yesterdayAvg,
        playersToday: today.playersToday,
        activeSpecials: today.activeSpecials ?? [],
      });
      return;
    }
    const base = {
      mode: 'daily' as RoundMode,
      sport: null,
      puzzleNo: today.puzzleNo,
      date: today.date,
      questions: today.questions,
      yesterdayAvg: today.yesterdayAvg,
      playersToday: today.playersToday,
      daily,
      special: null,
      activeSpecials: today.activeSpecials ?? [],
    };
    if (today.completed) {
      removeKey(STORAGE_KEYS.progress);
      set({
        ...base,
        phase: 'done',
        summary: today.completed,
        review: today.review,
        reveals: [],
        progressToken: null,
        position: QUESTIONS_PER_ROUND,
        pin: null,
        year: null,
      });
      return;
    }
    // Resume an in-progress round saved in localStorage (PRD §10.2).
    const stored = readJson(STORAGE_KEYS.progress, isStoredProgress);
    if (stored && stored.date === today.date && stored.reveals.length < QUESTIONS_PER_ROUND) {
      const inProgress = stored.reveals.length > 0;
      set({
        ...base,
        phase: inProgress ? 'where' : 'idle',
        progressToken: stored.progressToken,
        reveals: stored.reveals,
        position: stored.reveals.length + 1,
        pin: null,
        year: null,
        summary: null,
        review: null,
      });
      return;
    }
    removeKey(STORAGE_KEYS.progress);
    set({
      ...base,
      phase: 'idle',
      progressToken: today.progressToken,
      reveals: [],
      position: 1,
      pin: null,
      year: null,
      summary: null,
      review: null,
    });
    persistDaily({ date: today.date, progressToken: today.progressToken, reveals: [] });
  } catch (err) {
    if (!practiceActive(get)) set({ phase: 'error', error: message(err, 'Something went wrong') });
  }
}

async function loadPractice(
  set: Setter,
  fetchRound: () => Promise<{
    mode: RoundMode;
    sport: Sport | null;
    special?: SpecialInfo | null;
    date: string;
    puzzleNo: number;
    questions: PublicQuestion[];
    progressToken: string | null;
  }>,
  resumeMatches: (p: StoredPractice) => boolean,
): Promise<void> {
  set({ phase: 'loading', error: null });
  try {
    const stored = readJson(STORAGE_KEYS.practiceProgress, isStoredPractice);
    if (stored && resumeMatches(stored) && stored.reveals.length < QUESTIONS_PER_ROUND) {
      set({
        mode: stored.mode,
        sport: stored.sport,
        special: stored.special ?? null,
        date: stored.date,
        puzzleNo: stored.puzzleNo,
        questions: stored.questions,
        progressToken: stored.progressToken,
        reveals: stored.reveals,
        position: stored.reveals.length + 1,
        phase: 'where',
        pin: null,
        year: null,
        summary: null,
        review: null,
      });
      return;
    }
    const r = await fetchRound();
    const special = r.special ?? null;
    set({
      mode: r.mode,
      sport: r.sport,
      special,
      date: r.date,
      puzzleNo: r.puzzleNo,
      questions: r.questions,
      progressToken: r.progressToken,
      reveals: [],
      position: 1,
      phase: 'where',
      pin: null,
      year: null,
      summary: null,
      review: null,
    });
    persistPractice({ ...r, special, reveals: [] });
    track('practice_started', { mode: r.mode, sport: r.sport, special: special?.slug ?? null });
  } catch (err) {
    set({ phase: 'error', error: message(err, 'Could not start that round') });
  }
}

let inflight: Promise<void> | null = null;
let practiceKey: string | null = null;

/**
 * Practice rounds wait for the initial daily load: otherwise a first visit straight to
 * /play/random or /play/special/<slug> races two identity-less requests, each creates a player,
 * and the round's token belongs to the one that lost ("This round belongs to a different
 * player"). Play's effect runs before App's (children first), so when no load is in flight and
 * this browser has no player yet, start the daily load here and wait for it.
 */
async function startPractice(key: string, run: () => Promise<void>): Promise<void> {
  if (practiceKey === key) return;
  practiceKey = key;
  try {
    if (inflight) await inflight.catch(() => undefined);
    else if (!getPlayerToken()) await useRound.getState().load().catch(() => undefined);
    await run();
  } finally {
    practiceKey = null;
  }
}

export const useRound = create<RoundState>()((set, get) => ({
  ...initial,

  // Single-flight: concurrent calls (e.g. StrictMode double effects) must not create two players.
  load() {
    if (!inflight) {
      inflight = loadRound(set, get).finally(() => {
        inflight = null;
      });
    }
    return inflight;
  },

  start() {
    const s = get();
    if (s.phase === 'idle') {
      track('round_started', { puzzleNo: s.puzzleNo });
      set({ phase: 'where', pin: null, year: null });
    } else if (s.phase === 'where') {
      set({ pin: null, year: null });
    }
  },

  async startArchive(date) {
    const s = get();
    if (s.mode === 'archive' && s.date === date && s.phase !== 'done' && s.phase !== 'error')
      return;
    await startPractice(`archive:${date}`, () =>
      loadPractice(
        set,
        async () => {
          const backend = await getBackend();
          const r = await backend.archive(date);
          return {
            mode: 'archive',
            sport: null,
            date: r.date,
            puzzleNo: r.puzzleNo,
            questions: r.questions,
            progressToken: r.progressToken,
          };
        },
        (p) => p.mode === 'archive' && p.date === date,
      ),
    );
  },

  async startRandom(sport) {
    const s = get();
    if (
      s.mode === 'random' &&
      s.sport === sport &&
      (s.phase === 'where' || s.phase === 'when' || s.phase === 'reveal')
    )
      return;
    await startPractice(`random:${sport ?? 'all'}`, () =>
      loadPractice(
        set,
        async () => {
          const backend = await getBackend();
          const r = await backend.random(sport);
          return {
            mode: 'random',
            sport,
            date: r.date,
            puzzleNo: 0,
            questions: r.questions,
            progressToken: r.progressToken,
          };
        },
        (p) => p.mode === 'random' && p.sport === sport && !p.special,
      ),
    );
  },

  async startSpecial(slug) {
    const s = get();
    if (
      s.special?.slug === slug &&
      (s.phase === 'where' || s.phase === 'when' || s.phase === 'reveal')
    )
      return;
    await startPractice(`special:${slug}`, () =>
      loadPractice(
        set,
        async () => {
          const backend = await getBackend();
          const r = await backend.special(slug);
          return {
            mode: 'random',
            sport: r.sport,
            special: r.special ?? null,
            date: r.date,
            puzzleNo: 0,
            questions: r.questions,
            progressToken: r.progressToken,
          };
        },
        (p) => p.special?.slug === slug,
      ),
    );
  },

  /** Back to today's round after a practice round; reloads if today's token is unknown. */
  async exitPractice() {
    const s = get();
    if (s.mode === 'daily') return;
    removeKey(STORAGE_KEYS.practiceProgress);
    // Hand the screen back to the daily round before reloading it.
    set({
      mode: 'daily',
      sport: null,
      special: null,
      phase: 'boot',
      summary: null,
      review: null,
      reveals: [],
    });
    await get().load();
  },

  setPin(pin) {
    if (get().phase === 'where') set({ pin });
  },

  lockLocation() {
    const s = get();
    if (s.phase === 'where' && s.pin) {
      track('guess_where_locked', { position: s.position });
      set({ phase: 'when', year: null });
    }
  },

  setYear(year) {
    if (get().phase === 'when') set({ year });
  },

  async lockYear() {
    const s = get();
    if (s.phase !== 'when' || !s.pin || s.year === null || !s.progressToken) return;
    set({ phase: 'submitting', error: null });
    try {
      const backend = await getBackend();
      const res = await backend.guess({
        progressToken: s.progressToken,
        position: s.position,
        lat: s.pin.lat,
        lng: s.pin.lng,
        year: s.year,
      });
      const reveals = [...s.reveals, res.reveal];
      track('guess_submitted', {
        mode: s.mode,
        position: s.position,
        km: Math.round(res.reveal.distanceKm),
        yearDiff: res.reveal.yearDiff,
        pts: res.reveal.questionPts,
      });
      const review = res.summary ? reviewFromReveals(reveals, s.questions) : null;
      set({
        phase: 'reveal',
        reveals,
        progressToken: res.progressToken,
        summary: res.summary,
        review,
      });
      if (res.summary) {
        if (s.mode === 'daily') {
          removeKey(STORAGE_KEYS.progress);
          set({
            daily: {
              date: s.date,
              launchDate: s.daily?.launchDate ?? s.date,
              puzzleNo: s.puzzleNo,
              summary: res.summary,
              review,
            },
          });
          track('round_completed', { total: res.summary.total, timeMs: res.summary.timeMs });
        } else {
          removeKey(STORAGE_KEYS.practiceProgress);
          savePractice({
            summary: res.summary,
            review: review ?? [],
            finishedAt: new Date().toISOString(),
          });
          track('practice_completed', { mode: s.mode, total: res.summary.total });
        }
      } else if (s.mode === 'daily') {
        persistDaily({ date: s.date, progressToken: res.progressToken, reveals });
      } else {
        persistPractice({ ...s, progressToken: res.progressToken, reveals });
      }
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === 'token_expired' ||
          err.code === 'round_completed' ||
          err.code === 'round_expired' ||
          err.code === 'token_player_mismatch' ||
          err.code === 'invalid_token')
      ) {
        removeKey(s.mode === 'daily' ? STORAGE_KEYS.progress : STORAGE_KEYS.practiceProgress);
        set({ phase: 'error', error: err.message });
        return;
      }
      set({ phase: 'when', error: message(err, 'Could not submit your guess') });
    }
  },

  next() {
    const s = get();
    if (s.phase !== 'reveal') return;
    if (s.summary || s.position >= QUESTIONS_PER_ROUND) {
      set({ phase: 'done', pin: null, year: null });
      return;
    }
    set({ phase: 'where', position: s.position + 1, pin: null, year: null });
  },

  reset() {
    removeKey(STORAGE_KEYS.progress);
    removeKey(STORAGE_KEYS.practiceProgress);
    set({ ...initial });
  },
}));

export const selectRunningScore = (s: RoundState): number =>
  s.reveals.reduce((sum, r) => sum + r.questionPts, 0);

export const selectCurrentQuestion = (s: RoundState): PublicQuestion | undefined =>
  s.questions[s.position - 1];
